import useConfirm from "@/hooks/useConfirm";
import { transactionsKeys } from "@/query-keys";
import { services } from "@/services";
import type { TransactionListItem } from "@/schemas/transactionSchema";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useUserTransactionsQuery from "../_hooks/useUserTransactionsQuery";
import useColumns from "../../transactions/_hooks/useColumns";
import useTransactionMutation from "../../transactions/_hooks/useTransactionMutation";

export const Route = createFileRoute("/_private/users/$userId/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ params, deps, context: { queryClient } }) => {
    const { userId } = params;
    const requestParams = mapTableStateToQueryParams(safeDecompress(deps.q));
    requestParams.set("userId", userId);

    const transactions = await queryClient.ensureQueryData({
      queryKey: [...transactionsKeys.lists(deps.q), "user", userId],
      queryFn: () => services.getTransactions(requestParams),
    });

    return { transactions };
  },
  component: UserTransactionsPage,
});

function UserTransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { userId } = Route.useParams();
  const { transactions: initialTransactions } = Route.useLoaderData();
  const { data: transactions } = useUserTransactionsQuery(userId, search.q, initialTransactions);

  const columns = useColumns();
  const { deleteTransaction, isDeleting } = useTransactionMutation();

  const deleteConfirm = useConfirm({
    title: t("transactions.confirm.delete.title"),
    description: t("transactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (row: TransactionListItem) =>
      navigate({ to: "/transactions/$transactionId", params: { transactionId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: TransactionListItem) => {
      deleteConfirm.confirm(() => {
        deleteTransaction(row.id);
      });
    },
    [deleteConfirm, deleteTransaction],
  );

  return (
    <div className="flex-1 flex flex-col gap-8">
      <DataTable<TransactionListItem>
        queryKey={[...transactionsKeys.lists(search.q), "user", userId]}
        data={transactions?.items}
        nextCursor={transactions?.nextCursor}
        hasMore={transactions?.hasMore}
        columns={columns}
        searchPlaceholder={t("transactions.list.searchPlaceholder")}
        navigateFrom="/users/$userId"
        searchFrom="/_private/users/$userId/"
        onView={handleView}
        onDelete={handleDelete}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
